import { List, Page } from "@blacknut/javascript-sdk/dist";
import { AppLayout, State } from "core";
import {
  Focusable,
  FocusableSection,
  FocusableSectionProps,
  Config,
} from "@blacknut/spatialnav-sdk/dist";
import clsx from "clsx";
import * as React from "react";
import { useSelector } from "react-redux";
import { ReactComponent as Arrow } from "../../../assets/dist/ic_arrow.svg";
import dimens from "../../../theme/dimens";
import { tile2Id } from "../../../utils/Utils";
import Tile from "../../Tile/Tile";
import ArrowButton from "../ArrowButton";
import styles from "./styles.module.scss";
import { useProfiler } from "../../../hooks/useProfiler";
import { useMoreLink } from "src/utils/useMoreLink";
import useListTitleWithEntitlement from "../useListTitleWithEntitlement";

const SPATIAL_CONFIG: Config = { distanceMode: true, restrict: "self-first" };

interface TileListProps extends Pick<FocusableSectionProps, "leaveFor" | "disabled"> {
  showTitle?: boolean;
  list: List;
  className?: string;
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  scrollable?: boolean;
  onGenre?: boolean;
  page?: Page;
}

const FeaturedTileList = ({
  showTitle = true,
  list,
  className,
  leaveFor,
  disabled,
  onFocus,
  scrollable = false,
  page,
}: TileListProps) => {
  const { layout } = useSelector((state: State) => state.globalState);
  const { handleMoreLink } = useMoreLink(list.moreLink);
  const tiles = list.tiles;

  const { title } = useListTitleWithEntitlement(list);
  const hasTitle = showTitle && title && title.trim().length > 0;

  const Root = useProfiler("featuredList");
  return (
    <Root>
      <FocusableSection
        focusKey={list.uuid}
        className={clsx(styles.container, scrollable && styles.scrollable, className)}
        leaveFor={leaveFor}
        disabled={disabled}
        defaultElement={{ down: "first", up: ".tile" }}
        config={SPATIAL_CONFIG}
        onFocus={onFocus}
        onMouseDown={(e) => e.preventDefault()}
      >
        {(hasTitle || (!!list.moreLink && layout === AppLayout.PHONE)) && (
          <div className={clsx(styles.titleContainer, scrollable && styles.scrollable)}>
            {hasTitle && (
              <Focusable
                className={clsx(styles.title, !!list.moreLink && styles.clickable)}
                onPressEnter={handleMoreLink}
                onClick={handleMoreLink}
                disabled={
                  !list.moreLink || window.innerWidth >= dimens.breakpoints.desktop
                }
              >
                <span>{title}</span>
                <Arrow />
              </Focusable>
            )}
          </div>
        )}
        <div className={styles.content}>
          <ul className={clsx(styles.list, scrollable && styles.scrollable)}>
            {tiles.map((e, index) => (
              <li
                className={clsx(styles.tile, index === 0 && styles.featured)}
                key={`${list.uuid}-${tile2Id(e)}`}
              >
                <Tile
                  item={e}
                  list={list}
                  featured={index === 0}
                  page={page}
                  index={index}
                />
              </li>
            ))}
          </ul>

          {list.moreLink && (
            <ArrowButton
              onPress={handleMoreLink}
              className={clsx([styles.arrowButton, styles.arrow])}
            />
          )}
        </div>
      </FocusableSection>
    </Root>
  );
};

export default FeaturedTileList;
